import { ao3Assets } from "@/mocks/ao3Home";

export type Ao3Lang = "zh" | "en";

export type Ao3SeoEntry = {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  ogType: string;
  ogSiteName: string;
  ogTitle: string;
  ogDescription: string;
  twitterCard: string;
  twitterTitle: string;
  twitterDescription: string;
  image?: string;
  noindex?: boolean;
};

// Home SEO - Chinese version
const HOME_SEO_ZH: Ao3SeoEntry = {
  title: "AO3 - 同人创作随心畅读",
  description:
    "AO3是一款用于发现和管理同人作品的便捷阅读器，支持按角色、配对、标签与完成状态筛选内容，并可添加书签、稍后阅读及最近浏览。用户能够创建个人作品列表，随时找回正在追读的篇章，在丰富题材中不断发现感兴趣的新故事。",
  keywords: "AO3",
  canonicalPath: "/",
  ogType: "website",
  ogSiteName: "AO3",
  ogTitle: "AO3 - 同人创作随心畅读",
  ogDescription:
    "AO3是一款用于发现和管理同人作品的便捷阅读器，支持按角色、配对、标签与完成状态筛选内容，并可添加书签、稍后阅读及最近浏览。用户能够创建个人作品列表，随时找回正在追读的篇章，在丰富题材中不断发现感兴趣的新故事。",
  twitterCard: "summary_large_image",
  twitterTitle: "AO3 - 同人创作随心畅读",
  twitterDescription:
    "AO3是一款用于发现和管理同人作品的便捷阅读器。按角色、配对、标签与完成状态筛选，保存书签、稍后阅读与历史记录，创建个人作品列表，随时续读并发现新故事。",
  image: ao3Assets.heroBackground,
};

// Home SEO - English version
const HOME_SEO_EN: Ao3SeoEntry = {
  title: "AO3 - Read Fanfiction Freely",
  description:
    "AO3 is a convenient fanfiction reader. Filter by character, pairing, tag, and status. Save bookmarks, read-later lists, and history. Build personal lists, resume chapters, and discover new stories.",
  keywords: "AO3",
  canonicalPath: "/",
  ogType: "website",
  ogSiteName: "AO3",
  ogTitle: "AO3 - Read Fanfiction Freely",
  ogDescription:
    "AO3 is a convenient fanfiction reader. Filter by character, pairing, tag, and status. Save bookmarks, read-later lists, and history. Build personal lists, resume chapters, and discover new stories.",
  twitterCard: "summary_large_image",
  twitterTitle: "AO3 - Read Fanfiction Freely",
  twitterDescription:
    "AO3 is a convenient fanfiction reader. Filter by character, pairing, tag, and status. Save bookmarks, read-later lists, and history. Build personal lists, resume chapters, and discover new stories.",
  image: ao3Assets.heroBackground,
};

const HOME_SEO_BY_LANG: Record<Ao3Lang, Ao3SeoEntry> = {
  zh: HOME_SEO_ZH,
  en: HOME_SEO_EN,
};

const REPORT_SEO: Ao3SeoEntry = {
  title: "投诉举报",
  description: "/",
  keywords: "/",
  canonicalPath: "/report",
  ogType: "website",
  ogSiteName: "AO3",
  ogTitle: "投诉举报 - AO3",
  ogDescription:
    "如遇产品侵权、虚假官网、欺诈交易等问题，请通过 AO3 投诉举报通道如实反馈，我们会尽快核实处理。",
  twitterCard: "summary",
  twitterTitle: "投诉举报 - AO3",
  twitterDescription:
    "如遇产品侵权、虚假官网、欺诈交易等问题，请通过 AO3 投诉举报通道如实反馈，我们会尽快核实处理。",
  image: ao3Assets.logo,
};

const NOT_FOUND_SEO: Ao3SeoEntry = {
  title: "页面未找到 - AO3",
  description: "抱歉，你访问的页面不存在。返回 AO3 首页继续浏览。",
  keywords: "AO3",
  canonicalPath: "/",
  ogType: "website",
  ogSiteName: "AO3",
  ogTitle: "页面未找到 - AO3",
  ogDescription: "抱歉，你访问的页面不存在。返回 AO3 首页继续浏览。",
  twitterCard: "summary",
  twitterTitle: "页面未找到 - AO3",
  twitterDescription: "抱歉，你访问的页面不存在。返回 AO3 首页继续浏览。",
  image: ao3Assets.logo,
  noindex: true,
};

export const ao3SeoByRoute: Record<string, Ao3SeoEntry> = {
  "/report": REPORT_SEO,
};

export function ao3SiteOrigin(): string {
  const envUrl = import.meta.env.VITE_SITE_URL as string | undefined;
  if (envUrl && envUrl.trim()) {
    return envUrl.trim().replace(/\/+$/, "");
  }
  if (typeof window !== "undefined" && window.location) {
    return window.location.origin.replace(/\/+$/, "");
  }
  return "";
}

export function ao3AbsoluteUrl(path: string): string {
  const origin = ao3SiteOrigin();
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${origin}${clean}`;
}

export function ao3ResolveSeo(pathname: string, lang: Ao3Lang = "zh"): Ao3SeoEntry {
  if (pathname === "/") {
    return HOME_SEO_BY_LANG[lang] ?? HOME_SEO_ZH;
  }
  return ao3SeoByRoute[pathname] ?? NOT_FOUND_SEO;
}